import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { 
  getRateForRole, 
  getProjectYear
} from '../../../utils/rateUtils';
import { roleToFunctionMap, roleToFocusMap } from '../../../utils/wizardHelpers';

const defaultRoles = ["Product", "Tech Lead", "BA", "UX", "FE", "BE", "QA", "Other"];

const EditRoleDialog = ({
  open,
  onClose,
  editingRole,
  onSaveRole,
  locationOptions,
  roleOptions,
  outcomeData
}) => {
  const [roleForm, setRoleForm] = useState({
    name: '',
    company: '',
    location: '',
    function: '',
    role: '',
    rate: 0,
    rateAdjustmentInfo: null
  });

  // Initialize form when dialog opens
  useEffect(() => {
    if (open && editingRole?.role) {
      const role = editingRole.role;
      setRoleForm({
        name: role.name || '',
        company: role.company || 'CA',
        location: role.location || 'US',
        function: role.function || 'Other',
        role: role.role || '',
        rate: role.rate || 0,
        rateAdjustmentInfo: role.rateAdjustmentInfo || null
      });
    }
  }, [open, editingRole]);

  const handleFormChange = useCallback(async (field, value) => {
    setRoleForm(prev => {
      let updated = { ...prev, [field]: value };

      if (field === 'name') {
        updated.function = roleToFunctionMap[value] || "Other";
      }

      if (field === 'company') {
        const availableLocations = locationOptions[value] || [];
        updated.location = availableLocations.length > 0 ? availableLocations[0].code : 'US';
        updated.role = '';
      }

      if (field === 'location') {
        updated.role = '';
      }

      // Update rate when location/role changes
      if (['company', 'location', 'role'].includes(field)) {
        const projectYear = getProjectYear(outcomeData, null, new Date().getFullYear());
        getRateForRole(
          updated.company, 
          updated.location, 
          field === 'role' ? value : updated.role, 
          projectYear
        ).then(rateInfo => {
          if (rateInfo.monthlyRate > 0) {
            setRoleForm(current => ({
              ...current,
              rate: rateInfo.monthlyRate,
              rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
            }));
          }
        }).catch(error => {
          console.warn('Could not get COLA-adjusted rate:', error);
        });
      }

      return updated;
    });
  }, [locationOptions, roleOptions, outcomeData]);

  const handleSave = useCallback(() => {
    const updatedRole = {
      name: roleForm.name,
      company: roleForm.company,
      location: roleForm.location,
      function: roleForm.function,
      role: roleForm.role,
      rate: roleForm.rate,
      focus: roleForm.function === "Developer" ? (roleToFocusMap[roleForm.name] || "Fullstack") : undefined,
      rateAdjustmentInfo: roleForm.rateAdjustmentInfo,
      lastMetadataUpdate: new Date().toISOString()
    };
    
    onSaveRole(updatedRole);
  }, [roleForm, onSaveRole]);

  if (!editingRole) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Role: {editingRole.role?.name}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Role Name</InputLabel>
              <Select
                value={roleForm.name}
                label="Role Name"
                onChange={(e) => handleFormChange('name', e.target.value)}
              >
                {defaultRoles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={roleForm.company}
                label="Company"
                onChange={(e) => handleFormChange('company', e.target.value)}
              >
                {Object.keys(locationOptions).map(company => (
                  <MenuItem key={company} value={company}>{company}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={roleForm.location}
                label="Location"
                onChange={(e) => handleFormChange('location', e.target.value)}
              >
                {(locationOptions[roleForm.company] || []).map(location => (
                  <MenuItem key={location.code} value={location.code}>{location.display}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Specific Role</InputLabel>
              <Select
                value={roleForm.role}
                label="Specific Role"
                onChange={(e) => handleFormChange('role', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a role...</em>
                </MenuItem>
                {(roleOptions[roleForm.company]?.[roleForm.location] || []).map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Monthly Rate"
              type="number"
              value={roleForm.rate}
              onChange={(e) => handleFormChange('rate', Number(e.target.value))}
              InputProps={{
                startAdornment: '$'
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Function: {roleForm.function}
            </Typography>
            {roleForm.rateAdjustmentInfo && (
              <Typography variant="caption" color="warning.main">
                Rate includes COLA adjustment
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Changes</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRoleDialog;